import React, { useState, useEffect, useRef } from 'react';
import UseTypeSettings from './UseTypeSettings';
import { api } from '../api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_COLOR = {
  draft:     { bg: '#f8fafc', text: '#64748b', dot: '#cbd5e1' },
  enrolling: { bg: '#fffbeb', text: '#92400e', dot: '#fbbf24' },
  training:  { bg: '#eff6ff', text: '#1e40af', dot: '#3b82f6' },
  active:    { bg: '#f0fdf4', text: '#166534', dot: '#22c55e' },
  suspended: { bg: '#fef2f2', text: '#991b1b', dot: '#ef4444' },
};

const TASKS = [
  { id: 'intro_track', label: 'Station Intro',    icon: '🎙' },
  { id: 'sign_off',    label: 'Sign Off',          icon: '👋' },
  { id: 'tell_joke',   label: 'Tell a Joke',       icon: '😄' },
  { id: 'tell_story',  label: 'Tell a Story',      icon: '📖' },
  { id: 'run_segment', label: 'Run a Segment',     icon: '📻' },
  { id: 'ad_read',     label: 'Ad Read',            icon: '📢' },
  { id: 'news_toss',   label: 'News Toss',          icon: '📰' },
  { id: 'weather_hit', label: 'Weather Hit',        icon: '🌤' },
  { id: 'read_script', label: 'Read Script',        icon: '📄' },
];

function Badge({ status }) {
  const s = STATUS_COLOR[status] || STATUS_COLOR.draft;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 10px',
      borderRadius:20, fontSize:11, fontWeight:600, background:s.bg, color:s.text }}>
      <span style={{ width:6, height:6, borderRadius:'50%', background:s.dot, display:'inline-block' }} />
      {status}
    </span>
  );
}

function Card({ children, style }) {
  return (
    <div style={{ background:'#fff', borderRadius:14, border:'1px solid #f1f5f9',
      boxShadow:'0 1px 4px rgba(0,0,0,0.05)', padding:24, ...style }}>
      {children}
    </div>
  );
}

function Btn({ children, onClick, variant='primary', size='md', disabled, style }) {
  const base = { border:'none', borderRadius:9, fontWeight:600, cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1, transition:'all 0.15s', ...style };
  const sz  = size === 'sm' ? { padding:'6px 14px', fontSize:12 } : { padding:'10px 20px', fontSize:13 };
  const col = variant === 'primary'  ? { background:'#6366f1', color:'#fff' }
            : variant === 'danger'   ? { background:'#fee2e2', color:'#dc2626' }
            : variant === 'success'  ? { background:'#dcfce7', color:'#16a34a' }
            :                          { background:'#f1f5f9', color:'#374151' };
  return <button onClick={onClick} disabled={disabled} style={{...base,...sz,...col}}>{children}</button>;
}

function Input({ label, value, onChange, placeholder, type='text', multiline, rows=3 }) {
  const style = { width:'100%', padding:'9px 12px', border:'1px solid #e2e8f0', borderRadius:8,
    fontSize:13, color:'#0f172a', background:'#fff', outline:'none', boxSizing:'border-box',
    fontFamily:'inherit', resize: multiline ? 'vertical' : undefined };
  return (
    <div style={{ marginBottom:14 }}>
      {label && <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:5 }}>{label}</div>}
      {multiline
        ? <textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={rows} style={style} />
        : <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={style} />}
    </div>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', marginBottom:10 }}>
      <div onClick={()=>onChange(!checked)} style={{
        width:40, height:22, borderRadius:11, background: checked ? '#6366f1' : '#e2e8f0',
        position:'relative', cursor:'pointer', transition:'background 0.2s', flexShrink:0 }}>
        <div style={{ position:'absolute', top:2, left: checked ? 20 : 2, width:18, height:18,
          borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
      </div>
      <span style={{ fontSize:13, color:'#374151' }}>{label}</span>
    </label>
  );
}

// ─── Enrollment Iframe Modal ──────────────────────────────────────────────────

function EnrollmentModal({ persona, onClose, onEnrolled }) {
  const [sessionUrl, setSessionUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.post(`/broadcast/dbp/personas/${persona.id}/enroll`, {})
      .then(r => { setSessionUrl(r.session_url); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });

    const handler = (e) => {
      if (e.origin !== 'https://enroll.voiceseal.io') return;
      if (e.data?.status === 'enrolled') {
        onEnrolled(e.data.voice_id);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [persona.id, onEnrolled]);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex',
      alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div style={{ background:'#fff', borderRadius:16, width:640, maxHeight:'90vh', overflow:'hidden',
        display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'18px 24px', borderBottom:'1px solid #f1f5f9', display:'flex',
          alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontWeight:700, color:'#0f172a' }}>Voice Enrollment — {persona.voice_name}</div>
            <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>Powered by VoiceSeal • Biometric consent required</div>
          </div>
          <Btn onClick={onClose} variant='ghost' size='sm'>✕ Close</Btn>
        </div>
        <div style={{ flex:1, minHeight:480 }}>
          {loading && <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:480, color:'#64748b' }}>Loading enrollment session…</div>}
          {error && <div style={{ padding:24, color:'#dc2626' }}>Error: {error}</div>}
          {sessionUrl && !loading && (
            <iframe src={sessionUrl} style={{ width:'100%', height:480, border:'none' }} title="Voice Enrollment" />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Generate Clip Panel ──────────────────────────────────────────────────────

function GeneratePanel({ persona, onClose }) {
  const [task, setTask] = useState('intro_track');
  const [params, setParams] = useState({});
  const [script, setScript] = useState('');
  const [mode, setMode] = useState('locked');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  const isScriptTask = task === 'read_script';

  const generate = async () => {
    setGenerating(true); setResult(null); setError(null);
    try {
      let res;
      if (isScriptTask) {
        res = await api.post(`/broadcast/dbp/personas/${persona.id}/script`, { script, task, mode });
      } else {
        res = await api.post(`/broadcast/dbp/personas/${persona.id}/generate`, { task, task_params: params });
      }
      setResult(res);
    } catch(e) { setError(e.message); }
    finally { setGenerating(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex',
      alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div style={{ background:'#fff', borderRadius:16, width:580, maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ padding:'18px 24px', borderBottom:'1px solid #f1f5f9', display:'flex',
          alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ fontWeight:700, color:'#0f172a' }}>Generate Clip — {persona.voice_name}</div>
          <Btn onClick={onClose} variant='ghost' size='sm'>✕</Btn>
        </div>
        <div style={{ padding:24 }}>
          {/* Task selector */}
          <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:10 }}>Select Task</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginBottom:20 }}>
            {TASKS.map(t => (
              <button key={t.id} onClick={()=>setTask(t.id)} style={{
                padding:'10px 8px', borderRadius:9, border:`2px solid ${task===t.id ? '#6366f1' : '#e2e8f0'}`,
                background: task===t.id ? '#eef2ff' : '#fff', cursor:'pointer', textAlign:'center' }}>
                <div style={{ fontSize:18, marginBottom:3 }}>{t.icon}</div>
                <div style={{ fontSize:11, fontWeight:600, color: task===t.id ? '#6366f1' : '#374151' }}>{t.label}</div>
              </button>
            ))}
          </div>

          {/* Task params */}
          {task === 'run_segment' && (
            <Input label="Topic" value={params.topic||''} onChange={v=>setParams(p=>({...p,topic:v}))} placeholder="e.g. weekend events" />
          )}
          {task === 'tell_story' && (
            <Input label="Story Theme" value={params.story_theme||''} onChange={v=>setParams(p=>({...p,story_theme:v}))} placeholder="e.g. road trip mishap" />
          )}
          {task === 'ad_read' && (
            <Input label="Product / Service" value={params.product||''} onChange={v=>setParams(p=>({...p,product:v}))} placeholder="e.g. Java House Coffee" />
          )}
          {task === 'weather_hit' && (
            <Input label="Weather Conditions" value={params.weather_conditions||''} onChange={v=>setParams(p=>({...p,weather_conditions:v}))} placeholder="e.g. sunny 72°F" />
          )}
          {isScriptTask && (
            <>
              <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                {['locked','guided'].map(m => (
                  <button key={m} onClick={()=>setMode(m)} style={{
                    padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer',
                    border:`2px solid ${mode===m ? '#6366f1' : '#e2e8f0'}`,
                    background: mode===m ? '#eef2ff' : '#fff', color: mode===m ? '#6366f1' : '#374151' }}>
                    {m === 'locked' ? '🔒 Verbatim' : '🎨 Guided'}
                  </button>
                ))}
              </div>
              <div style={{ fontSize:11, color:'#64748b', marginBottom:12 }}>
                {mode==='locked' ? 'Persona reads exactly as written.' : 'Script is a blueprint — persona adds their personality.'}
              </div>
              <Input label="Script" value={script} onChange={setScript} placeholder="Enter script here…" multiline rows={5} />
            </>
          )}

          {/* Stand-in notice */}
          {persona.elevenlabs_clone_status !== 'ready' && (
            <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:9, padding:12, marginBottom:16, fontSize:12, color:'#92400e' }}>
              ⚡ Using stand-in voice. Upload audio via <strong>Clone Voice</strong> to use your actual voice.
            </div>
          )}

          <Btn onClick={generate} disabled={generating || (isScriptTask && !script.trim())} style={{ width:'100%' }}>
            {generating ? 'Generating…' : 'Generate Clip'}
          </Btn>

          {error && <div style={{ marginTop:12, padding:12, background:'#fef2f2', borderRadius:8, fontSize:12, color:'#dc2626' }}>{error}</div>}

          {result && (
            <div style={{ marginTop:16, background:'#f8fafc', borderRadius:10, padding:16 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#374151', marginBottom:8 }}>
                ✅ Clip Generated · {result.duration_seconds}s · {result.voice_used === 'cloned' ? 'Cloned Voice' : 'Stand-in Voice'}
              </div>
              {result.script && (
                <div style={{ fontSize:12, color:'#475569', lineHeight:1.6, marginBottom:12,
                  background:'#fff', borderRadius:8, padding:12, border:'1px solid #e2e8f0' }}>
                  <em>"{result.script}"</em>
                </div>
              )}
              {result.audio_url && result.audio_url.startsWith('http') && (
                <audio ref={audioRef} controls src={result.audio_url} style={{ width:'100%' }} />
              )}
              {result.restriction_flagged && (
                <div style={{ marginTop:8, fontSize:11, color:'#92400e', background:'#fffbeb', padding:8, borderRadius:6 }}>
                  ⚠️ Content flagged: {result.flagged_categories?.join(', ')}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Training Upload Panel ────────────────────────────────────────────────────

function TrainingPanel({ persona, onClose, onTrained }) {
  const [file, setFile] = useState(null);
  const [inputType, setInputType] = useState('broadcast_recording');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const upload = async () => {
    if (!file) return;
    setUploading(true); setError(null); setResult(null);
    try {
      const token = localStorage.getItem('broadcast_token');
      const BASE = process.env.REACT_APP_API_URL || 'https://groovie-business-api-production.up.railway.app/api/v1';
      const form = new FormData();
      form.append('audio_file', file);
      form.append('input_type', inputType);
      const res = await fetch(`${BASE}/broadcast/dbp/personas/${persona.id}/train`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.detail || 'Upload failed'); }
      const data = await res.json();
      setResult(data);
      if (data.applied) onTrained();
    } catch(e) { setError(e.message); }
    finally { setUploading(false); }
  };

  const cloneVoice = async () => {
    if (!file) return;
    setUploading(true); setError(null);
    try {
      const token = localStorage.getItem('broadcast_token');
      const BASE = process.env.REACT_APP_API_URL || 'https://groovie-business-api-production.up.railway.app/api/v1';
      const form = new FormData();
      form.append('audio_file', file);
      const res = await fetch(`${BASE}/broadcast/dbp/personas/${persona.id}/clone`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.detail || 'Clone failed'); }
      const data = await res.json();
      setResult({ ...data, isClone: true });
      onTrained();
    } catch(e) { setError(e.message); }
    finally { setUploading(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex',
      alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div style={{ background:'#fff', borderRadius:16, width:520 }}>
        <div style={{ padding:'18px 24px', borderBottom:'1px solid #f1f5f9', display:'flex',
          alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ fontWeight:700, color:'#0f172a' }}>Upload Audio — {persona.voice_name}</div>
          <Btn onClick={onClose} variant='ghost' size='sm'>✕</Btn>
        </div>
        <div style={{ padding:24 }}>
          <div style={{ fontSize:12, color:'#64748b', marginBottom:16, lineHeight:1.6 }}>
            Upload past broadcast audio to train your AI persona. Raw audio is never stored — transcribed and deleted within 24hrs (BIPA compliant).
          </div>

          <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:8 }}>Recording Type</div>
          <div style={{ display:'flex', gap:8, marginBottom:16 }}>
            {[{id:'broadcast_recording',label:'On-Air Recording'},{id:'studio_session',label:'Studio Session'},{id:'script_reading',label:'Script Reading'}].map(t=>(
              <button key={t.id} onClick={()=>setInputType(t.id)} style={{
                padding:'6px 12px', borderRadius:8, fontSize:11, fontWeight:600, cursor:'pointer',
                border:`2px solid ${inputType===t.id ? '#6366f1' : '#e2e8f0'}`,
                background: inputType===t.id ? '#eef2ff' : '#fff', color: inputType===t.id ? '#6366f1' : '#374151' }}>
                {t.label}
              </button>
            ))}
          </div>

          <div style={{ border:'2px dashed #e2e8f0', borderRadius:10, padding:24, textAlign:'center', marginBottom:16,
            background: file ? '#f0fdf4' : '#f8fafc', cursor:'pointer' }}
            onClick={()=>document.getElementById('audio-upload').click()}>
            <input id="audio-upload" type="file" accept="audio/*" style={{ display:'none' }}
              onChange={e=>setFile(e.target.files[0])} />
            <div style={{ fontSize:28, marginBottom:8 }}>🎵</div>
            {file
              ? <div style={{ fontSize:13, fontWeight:600, color:'#16a34a' }}>{file.name}</div>
              : <div style={{ fontSize:13, color:'#94a3b8' }}>Click to select audio file (MP3, WAV, M4A)</div>}
          </div>

          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={upload} disabled={!file || uploading} style={{ flex:1 }}>
              {uploading ? 'Processing…' : '🧠 Train Persona'}
            </Btn>
            <Btn onClick={cloneVoice} disabled={!file || uploading} variant='ghost' style={{ flex:1 }}>
              {uploading ? 'Cloning…' : '🎤 Clone Voice'}
            </Btn>
          </div>

          {error && <div style={{ marginTop:12, padding:12, background:'#fef2f2', borderRadius:8, fontSize:12, color:'#dc2626' }}>{error}</div>}

          {result && (
            <div style={{ marginTop:12, background:'#f0fdf4', borderRadius:8, padding:12, fontSize:12, color:'#166534' }}>
              {result.isClone
                ? <>✅ Voice cloned successfully! Clone status: <strong>{result.clone_status}</strong></>
                : <>✅ Training complete · {result.transcript_word_count} words · {result.duration_seconds?.toFixed(0)}s
                    {result.applied ? ' · Profile updated' : ' · Pending consent review'}</>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Create Persona Modal ─────────────────────────────────────────────────────

function CreatePersonaModal({ onClose, onCreate }) {
  const [form, setForm] = useState({
    name: '', voice_name: '', voice_gender: 'neutral', description: '',
  });
  const [filters, setFilters] = useState({
    restrict_racial_content: false, restrict_political_content: false,
    restrict_religious_content: false, restrict_controversial_content: false,
    restriction_action: 'block',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const save = async () => {
    if (!form.name || !form.voice_name) { setError('Name and On-Air Name are required'); return; }
    setSaving(true); setError(null);
    try {
      const res = await api.post('/broadcast/dbp/personas', { ...form, content_filters: filters });
      onCreate(res.persona);
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex',
      alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div style={{ background:'#fff', borderRadius:16, width:520, maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ padding:'18px 24px', borderBottom:'1px solid #f1f5f9', display:'flex',
          alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ fontWeight:700, color:'#0f172a' }}>Create New Persona</div>
          <Btn onClick={onClose} variant='ghost' size='sm'>✕</Btn>
        </div>
        <div style={{ padding:24 }}>
          <Input label="Persona Name" value={form.name} onChange={v=>set('name',v)} placeholder="e.g. Marcus Blaze" />
          <Input label="On-Air Voice Name" value={form.voice_name} onChange={v=>set('voice_name',v)} placeholder="Name listeners will hear" />

          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:5 }}>Voice Gender</div>
            <div style={{ display:'flex', gap:8 }}>
              {['male','female','neutral'].map(g=>(
                <button key={g} onClick={()=>set('voice_gender',g)} style={{
                  padding:'6px 16px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer',
                  border:`2px solid ${form.voice_gender===g ? '#6366f1' : '#e2e8f0'}`,
                  background: form.voice_gender===g ? '#eef2ff' : '#fff',
                  color: form.voice_gender===g ? '#6366f1' : '#374151' }}>
                  {g.charAt(0).toUpperCase()+g.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <Input label="Description (optional)" value={form.description} onChange={v=>set('description',v)} placeholder="Brief persona description" multiline rows={2} />

          <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:10, marginTop:4 }}>Content Restrictions</div>
          <div style={{ background:'#f8fafc', borderRadius:10, padding:14, marginBottom:16 }}>
            <Toggle checked={filters.restrict_racial_content} onChange={v=>setFilters(p=>({...p,restrict_racial_content:v}))} label="Restrict racial content" />
            <Toggle checked={filters.restrict_political_content} onChange={v=>setFilters(p=>({...p,restrict_political_content:v}))} label="Restrict political content" />
            <Toggle checked={filters.restrict_religious_content} onChange={v=>setFilters(p=>({...p,restrict_religious_content:v}))} label="Restrict religious content" />
            <Toggle checked={filters.restrict_controversial_content} onChange={v=>setFilters(p=>({...p,restrict_controversial_content:v}))} label="Restrict controversial content" />
            {(filters.restrict_racial_content||filters.restrict_political_content||filters.restrict_religious_content||filters.restrict_controversial_content) && (
              <div style={{ marginTop:10 }}>
                <div style={{ fontSize:11, fontWeight:600, color:'#374151', marginBottom:6 }}>When flagged content detected:</div>
                <div style={{ display:'flex', gap:8 }}>
                  {[{id:'block',label:'Block generation'},{id:'require_consent',label:'Request owner consent'}].map(a=>(
                    <button key={a.id} onClick={()=>setFilters(p=>({...p,restriction_action:a.id}))} style={{
                      padding:'5px 12px', borderRadius:7, fontSize:11, fontWeight:600, cursor:'pointer',
                      border:`2px solid ${filters.restriction_action===a.id ? '#6366f1' : '#e2e8f0'}`,
                      background: filters.restriction_action===a.id ? '#eef2ff' : '#fff',
                      color: filters.restriction_action===a.id ? '#6366f1' : '#374151' }}>
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {error && <div style={{ marginBottom:12, padding:10, background:'#fef2f2', borderRadius:8, fontSize:12, color:'#dc2626' }}>{error}</div>}
          <Btn onClick={save} disabled={saving} style={{ width:'100%' }}>{saving ? 'Creating…' : 'Create Persona'}</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── Persona Detail Panel ─────────────────────────────────────────────────────

function PersonaDetail({ persona, onClose, onRefresh }) {
  const [tab, setTab] = useState('overview');
  const [clips, setClips] = useState([]);
  const [training, setTraining] = useState([]);
  const [showGenerate, setShowGenerate] = useState(false);
  const [showTraining, setShowTraining] = useState(false);
  const [showEnroll, setShowEnroll] = useState(false);
  const [enrollStatus, setEnrollStatus] = useState(null);

  useEffect(() => {
    api.get(`/broadcast/dbp/personas/${persona.id}/clips`).then(r=>setClips(r.clips||[])).catch(()=>{});
    api.get(`/broadcast/dbp/personas/${persona.id}/training`).then(r=>setTraining(r.training_records||[])).catch(()=>{});
    api.get(`/broadcast/dbp/personas/${persona.id}/enroll`).then(r=>setEnrollStatus(r)).catch(()=>{});
  }, [persona.id]);

  const tabStyle = (t) => ({
    padding:'8px 16px', fontSize:13, fontWeight:600, cursor:'pointer', border:'none',
    background:'transparent', borderBottom:`2px solid ${tab===t ? '#6366f1' : 'transparent'}`,
    color: tab===t ? '#6366f1' : '#64748b', transition:'all 0.15s'
  });

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex',
      alignItems:'center', justifyContent:'center', zIndex:999 }}>
      <div style={{ background:'#fff', borderRadius:16, width:700, maxHeight:'90vh',
        display:'flex', flexDirection:'column', overflow:'hidden' }}>

        {/* Header */}
        <div style={{ padding:'18px 24px', borderBottom:'1px solid #f1f5f9',
          display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ width:44, height:44, borderRadius:'50%', background:'linear-gradient(135deg,#6366f1,#8b5cf6)',
              display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>🎙</div>
            <div>
              <div style={{ fontWeight:800, fontSize:16, color:'#0f172a' }}>{persona.voice_name}</div>
              <div style={{ fontSize:12, color:'#64748b' }}>{persona.name}</div>
            </div>
            <Badge status={persona.status} />
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <Btn onClick={()=>setShowTraining(true)} variant='ghost' size='sm'>🎵 Upload Audio</Btn>
            <Btn onClick={()=>setShowGenerate(true)} size='sm'
              disabled={persona.status==='suspended'}>⚡ Generate</Btn>
            <Btn onClick={onClose} variant='ghost' size='sm'>✕</Btn>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', borderBottom:'1px solid #f1f5f9', padding:'0 24px' }}>
          {['overview','clips','training','settings'].map(t=>(
            <button key={t} style={tabStyle(t)} onClick={()=>setTab(t)}>
              {t.charAt(0).toUpperCase()+t.slice(1)}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto', padding:24 }}>

          {tab==='overview' && (
            <div>
              {/* Voice status */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:20 }}>
                <div style={{ background:'#f8fafc', borderRadius:10, padding:14 }}>
                  <div style={{ fontSize:11, fontWeight:600, color:'#64748b', marginBottom:6 }}>VOICESEAL</div>
                  <div style={{ fontSize:13, fontWeight:700, color: persona.voiceseal_enrolled ? '#16a34a' : '#94a3b8' }}>
                    {persona.voiceseal_enrolled ? '✅ Enrolled' : '⬜ Not Enrolled'}
                  </div>
                  {!persona.voiceseal_enrolled && (
                    <Btn onClick={()=>setShowEnroll(true)} size='sm' style={{ marginTop:8 }}>Enroll Voice</Btn>
                  )}
                </div>
                <div style={{ background:'#f8fafc', borderRadius:10, padding:14 }}>
                  <div style={{ fontSize:11, fontWeight:600, color:'#64748b', marginBottom:6 }}>VOICE CLONE</div>
                  <div style={{ fontSize:13, fontWeight:700, color: persona.elevenlabs_clone_status==='ready' ? '#16a34a' : '#94a3b8' }}>
                    {persona.elevenlabs_clone_status==='ready' ? '✅ Cloned' : persona.elevenlabs_clone_status==='pending' ? '⏳ Processing' : '⬜ Not Cloned'}
                  </div>
                  {persona.elevenlabs_clone_status!=='ready' && (
                    <div style={{ fontSize:11, color:'#94a3b8', marginTop:4 }}>Upload audio to clone voice</div>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
                {[
                  { label:'Training Sessions', value: persona.training_data_count || 0, icon:'🧠' },
                  { label:'Clips Generated', value: persona.total_clips || clips.length, icon:'🎵' },
                  { label:'Last Trained', value: persona.last_trained_at ? new Date(persona.last_trained_at).toLocaleDateString() : 'Never', icon:'📅' },
                ].map(s=>(
                  <div key={s.label} style={{ background:'#f8fafc', borderRadius:10, padding:14, textAlign:'center' }}>
                    <div style={{ fontSize:22, marginBottom:4 }}>{s.icon}</div>
                    <div style={{ fontSize:20, fontWeight:800, color:'#0f172a' }}>{s.value}</div>
                    <div style={{ fontSize:11, color:'#64748b' }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Persona traits preview */}
              {persona.persona_profile && Object.keys(persona.persona_profile).length > 0 && (
                <div style={{ background:'#f8fafc', borderRadius:10, padding:14 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'#374151', marginBottom:10 }}>Persona Profile</div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:8 }}>
                    {['humor_style','joke_style','pacing','energy_level','tone','script_adherence'].map(k=>(
                      persona.persona_profile[k] ? (
                        <div key={k} style={{ fontSize:12 }}>
                          <span style={{ color:'#94a3b8' }}>{k.replace(/_/g,' ')}: </span>
                          <span style={{ color:'#374151', fontWeight:600 }}>{persona.persona_profile[k]}</span>
                        </div>
                      ) : null
                    ))}
                  </div>
                  {persona.persona_profile.catchphrases?.length > 0 && (
                    <div style={{ marginTop:10, fontSize:12 }}>
                      <span style={{ color:'#94a3b8' }}>catchphrases: </span>
                      <span style={{ color:'#6366f1', fontStyle:'italic' }}>"{persona.persona_profile.catchphrases.slice(0,3).join('" · "')}"</span>
                    </div>
                  )}
                </div>
              )}

              {/* Fingerprint status if enrolling */}
              {enrollStatus && enrollStatus.fingerprint_status && (
                <div style={{ marginTop:12, background:'#eff6ff', borderRadius:10, padding:12, fontSize:12, color:'#1e40af' }}>
                  🔍 Fingerprint: {enrollStatus.fingerprint_status}
                  {enrollStatus.fingerprint_progress > 0 && ` · ${enrollStatus.fingerprint_progress}% complete`}
                </div>
              )}
            </div>
          )}

          {tab==='clips' && (
            <div>
              {clips.length === 0
                ? <div style={{ textAlign:'center', color:'#94a3b8', padding:40 }}>No clips generated yet.<br/>Hit ⚡ Generate to create your first clip.</div>
                : clips.map(c=>(
                  <div key={c.id} style={{ background:'#f8fafc', borderRadius:10, padding:14, marginBottom:10 }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ fontSize:16 }}>{TASKS.find(t=>t.id===c.usage_type)?.icon || '🎵'}</span>
                        <span style={{ fontSize:13, fontWeight:600, color:'#374151' }}>{TASKS.find(t=>t.id===c.usage_type)?.label || c.usage_type}</span>
                        {c.voiceseal_watermark && <span style={{ fontSize:10, background:'#dcfce7', color:'#16a34a', padding:'2px 7px', borderRadius:10, fontWeight:600 }}>Watermarked</span>}
                      </div>
                      <span style={{ fontSize:11, color:'#94a3b8' }}>{c.duration_seconds?.toFixed(1)}s · {new Date(c.created_at).toLocaleDateString()}</span>
                    </div>
                    {c.script_text && <div style={{ fontSize:12, color:'#64748b', fontStyle:'italic', marginBottom:8 }}>"{c.script_text?.slice(0,120)}{c.script_text?.length>120?'…':''}"</div>}
                    {c.audio_url?.startsWith('http') && <audio controls src={c.audio_url} style={{ width:'100%', height:32 }} />}
                  </div>
                ))}
            </div>
          )}

          {tab==='training' && (
            <div>
              {training.length === 0
                ? <div style={{ textAlign:'center', color:'#94a3b8', padding:40 }}>No training sessions yet.<br/>Upload broadcast audio to train your persona.</div>
                : training.map(t=>(
                  <div key={t.id} style={{ background:'#f8fafc', borderRadius:10, padding:14, marginBottom:10 }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ fontSize:13, fontWeight:600, color:'#374151' }}>{t.input_type?.replace(/_/g,' ')}</span>
                        <span style={{ fontSize:10, background: t.applied ? '#dcfce7' : '#fef3c7',
                          color: t.applied ? '#16a34a' : '#92400e', padding:'2px 7px', borderRadius:10, fontWeight:600 }}>
                          {t.applied ? 'Applied' : 'Pending'}
                        </span>
                        {t.restriction_flagged && <span style={{ fontSize:10, background:'#fef2f2', color:'#dc2626', padding:'2px 7px', borderRadius:10, fontWeight:600 }}>Flagged</span>}
                      </div>
                      <span style={{ fontSize:11, color:'#94a3b8' }}>{t.duration_seconds?.toFixed(0)}s · {new Date(t.created_at).toLocaleDateString()}</span>
                    </div>
                    {t.restriction_flagged && t.restriction_categories?.length > 0 && (
                      <div style={{ fontSize:11, color:'#dc2626', marginTop:6 }}>Categories: {t.restriction_categories.join(', ')}</div>
                    )}
                    {!t.applied && !t.restriction_flagged && (
                      <Btn onClick={()=>api.post(`/broadcast/dbp/personas/${persona.id}/training/${t.id}/apply`,{}).then(onRefresh)} size='sm' variant='success' style={{ marginTop:8 }}>Apply Training</Btn>
                    )}
                  </div>
                ))}
            </div>
          )}

          {tab==='settings' && (
            <div>
              <ContentRestrictionsSettings persona={persona} onSaved={onRefresh} />
              <div style={{ marginTop:24, paddingTop:24, borderTop:'1px solid #f1f5f9' }}>
                <UseTypeSettings personaId={persona.id} voiceName={persona.voice_name} onSaved={onRefresh} />
              </div>
            </div>
          )}
        </div>
      </div>

      {showGenerate && <GeneratePanel persona={persona} onClose={()=>setShowGenerate(false)} />}
      {showTraining && <TrainingPanel persona={persona} onClose={()=>setShowTraining(false)} onTrained={()=>{setShowTraining(false);onRefresh();}} />}
      {showEnroll && <EnrollmentModal persona={persona} onClose={()=>setShowEnroll(false)} onEnrolled={(vid)=>{setShowEnroll(false);onRefresh();}} />}
    </div>
  );
}

// ─── Content Restrictions Settings ───────────────────────────────────────────

function ContentRestrictionsSettings({ persona, onSaved }) {
  const [filters, setFilters] = useState({ ...(persona.content_filters||{}) });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await api.put(`/broadcast/dbp/personas/${persona.id}`, { content_filters: filters });
      setSaved(true); onSaved();
      setTimeout(()=>setSaved(false), 2000);
    } catch(e) {}
    finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{ fontSize:14, fontWeight:700, color:'#0f172a', marginBottom:16 }}>Content Restrictions</div>
      <div style={{ background:'#f8fafc', borderRadius:10, padding:16, marginBottom:16 }}>
        <Toggle checked={!!filters.restrict_racial_content} onChange={v=>setFilters(p=>({...p,restrict_racial_content:v}))} label="Restrict racial content" />
        <Toggle checked={!!filters.restrict_political_content} onChange={v=>setFilters(p=>({...p,restrict_political_content:v}))} label="Restrict political content" />
        <Toggle checked={!!filters.restrict_religious_content} onChange={v=>setFilters(p=>({...p,restrict_religious_content:v}))} label="Restrict religious content" />
        <Toggle checked={!!filters.restrict_controversial_content} onChange={v=>setFilters(p=>({...p,restrict_controversial_content:v}))} label="Restrict controversial content" />

        {(filters.restrict_racial_content||filters.restrict_political_content||filters.restrict_religious_content||filters.restrict_controversial_content) && (
          <div style={{ marginTop:12, paddingTop:12, borderTop:'1px solid #e2e8f0' }}>
            <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:8 }}>When flagged content detected:</div>
            <div style={{ display:'flex', gap:8 }}>
              {[{id:'block',label:'🚫 Block generation'},{id:'require_consent',label:'✋ Require my consent'}].map(a=>(
                <button key={a.id} onClick={()=>setFilters(p=>({...p,restriction_action:a.id}))} style={{
                  padding:'7px 14px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer',
                  border:`2px solid ${filters.restriction_action===a.id ? '#6366f1' : '#e2e8f0'}`,
                  background: filters.restriction_action===a.id ? '#eef2ff' : '#fff',
                  color: filters.restriction_action===a.id ? '#6366f1' : '#374151' }}>
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <Btn onClick={save} disabled={saving} style={{ width:'100%' }}>
        {saving ? 'Saving…' : saved ? '✅ Saved' : 'Save Restrictions'}
      </Btn>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Personas() {
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [consentDone, setConsentDone] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/broadcast/dbp/personas')
      .then(r => setPersonas(r.personas || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const submitConsent = async () => {
    setConsentLoading(true);
    try {
      await api.post('/broadcast/dbp/consent', { full_name: 'Broadcaster', email: 'broadcaster@groovie.live' });
      setConsentDone(true);
    } catch(e) { setConsentDone(true); } // already on file is fine
    finally { setConsentLoading(false); }
  };

  return (
    <div style={{ maxWidth:1100, margin:'0 auto' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:28 }}>
        <div>
          <h1 style={{ fontSize:24, fontWeight:800, color:'#0f172a', margin:0 }}>Digitized Broadcast Personas</h1>
          <p style={{ fontSize:13, color:'#64748b', margin:'4px 0 0' }}>
            AI voice personas trained on your broadcast style · Powered by VoiceSeal
          </p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          {!consentDone && (
            <Btn onClick={submitConsent} disabled={consentLoading} variant='ghost'>
              {consentLoading ? 'Submitting…' : '📋 Submit Consent'}
            </Btn>
          )}
          <Btn onClick={()=>setShowCreate(true)}>+ New Persona</Btn>
        </div>
      </div>

      {/* Consent banner */}
      {!consentDone && personas.length === 0 && (
        <Card style={{ marginBottom:20, background:'#fffbeb', border:'1px solid #fde68a' }}>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <div style={{ fontSize:28 }}>📋</div>
            <div>
              <div style={{ fontWeight:700, color:'#92400e', marginBottom:4 }}>Biometric Consent Required</div>
              <div style={{ fontSize:13, color:'#92400e' }}>
                Submit consent before enrolling your voice. Required by BIPA for biometric data processing.
              </div>
            </div>
            <Btn onClick={submitConsent} disabled={consentLoading} style={{ flexShrink:0 }}>
              {consentLoading ? 'Submitting…' : 'Submit Now'}
            </Btn>
          </div>
        </Card>
      )}

      {/* Personas grid */}
      {loading ? (
        <div style={{ textAlign:'center', padding:60, color:'#94a3b8' }}>Loading personas…</div>
      ) : personas.length === 0 ? (
        <Card style={{ textAlign:'center', padding:60 }}>
          <div style={{ fontSize:48, marginBottom:16 }}>🎙</div>
          <div style={{ fontSize:18, fontWeight:700, color:'#0f172a', marginBottom:8 }}>No personas yet</div>
          <div style={{ fontSize:14, color:'#64748b', marginBottom:24 }}>
            Create your first AI broadcast persona — trained on your voice and style.
          </div>
          <Btn onClick={()=>setShowCreate(true)}>Create First Persona</Btn>
        </Card>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:16 }}>
          {personas.map(p => (
            <Card key={p.id} style={{ cursor:'pointer', transition:'box-shadow 0.15s' }}
              onClick={()=>setSelected(p)}
              onMouseEnter={e=>e.currentTarget.style.boxShadow='0 4px 20px rgba(99,102,241,0.15)'}
              onMouseLeave={e=>e.currentTarget.style.boxShadow='0 1px 4px rgba(0,0,0,0.05)'}>

              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:14 }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ width:44, height:44, borderRadius:'50%',
                    background:'linear-gradient(135deg,#6366f1,#8b5cf6)',
                    display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>🎙</div>
                  <div>
                    <div style={{ fontWeight:800, fontSize:15, color:'#0f172a' }}>{p.voice_name}</div>
                    <div style={{ fontSize:12, color:'#64748b' }}>{p.name}</div>
                  </div>
                </div>
                <Badge status={p.status} />
              </div>

              {/* Voice pipeline status */}
              <div style={{ display:'flex', gap:6, marginBottom:14 }}>
                {[
                  { label:'VoiceSeal', ok: p.voiceseal_enrolled },
                  { label:'Cloned', ok: p.elevenlabs_clone_status==='ready' },
                  { label:'Trained', ok: (p.training_data_count||0)>0 },
                ].map(item=>(
                  <span key={item.label} style={{ fontSize:10, fontWeight:600, padding:'3px 8px', borderRadius:8,
                    background: item.ok ? '#f0fdf4' : '#f8fafc',
                    color: item.ok ? '#16a34a' : '#94a3b8',
                    border:`1px solid ${item.ok ? '#bbf7d0' : '#e2e8f0'}` }}>
                    {item.ok ? '✓' : '○'} {item.label}
                  </span>
                ))}
              </div>

              {/* Profile summary */}
              {p.persona_profile?.energy_level && (
                <div style={{ fontSize:12, color:'#64748b', display:'flex', gap:12, marginBottom:14 }}>
                  <span>⚡ {p.persona_profile.energy_level}</span>
                  <span>🎭 {p.persona_profile.humor_style}</span>
                  <span>🎙 {p.persona_profile.pacing}</span>
                </div>
              )}

              <div style={{ display:'flex', gap:8 }}>
                <Btn size='sm' style={{ flex:1 }} onClick={e=>{e.stopPropagation();setSelected(p);}}>Open</Btn>
                <Btn size='sm' variant='ghost'
                  disabled={p.status==='suspended' || p.elevenlabs_clone_status!=='ready'}
                  onClick={e=>{e.stopPropagation();/* TODO: assign to shift */}}>
                  📡 Assign
                </Btn>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modals */}
      {showCreate && (
        <CreatePersonaModal
          onClose={()=>setShowCreate(false)}
          onCreate={(p)=>{ setPersonas(prev=>[p,...prev]); setShowCreate(false); setSelected(p); }}
        />
      )}
      {selected && (
        <PersonaDetail
          persona={personas.find(p=>p.id===selected.id)||selected}
          onClose={()=>setSelected(null)}
          onRefresh={()=>{ load(); }}
        />
      )}
    </div>
  );
}
